import gdadSkillsMappingJson from '../config/pages/gdad/data/skills-mapping.json';

type GdadRoleSkill = {
  skill_id: string;
  order?: number;
};

type GdadSkillsMapping = {
  role_level_skills_map: Record<string, GdadRoleSkill[]>;
};

export type GdadProficiencyDerivedAnswer = {
  name: 'your-proficiency-score' | 'proficiency-level';
  title: 'Your Proficiency Score' | 'Proficiency Level';
  value: string;
};

const gdadSkillsMapping = gdadSkillsMappingJson as GdadSkillsMapping;

const levelDescriptionByName: Record<string, string> = {
  Developing:
    'Works towards most of the required skill levels for the relevant skills in the Capability Framework',
  'Proficient A':
    'Works at most of the required skill levels for the relevant skills in the Capability Framework',
  'Proficient B':
    'Works at all the required skills levels for the relevant skills on the Capability Framework',
  'Accomplished A':
    'Works above most of the required skill levels for the relevant skills on the Capability Framework',
  'Accomplished B':
    'Works above all the required skills levels for the relevant skills on the Capability Framework',
};

export const getGdadSkillScore = (value: unknown): number => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 3) {
    return value;
  }

  const text = String(value ?? '').trim();
  if (!/^[0-3]$/.test(text)) return 0;
  return Number.parseInt(text, 10);
};

const toOrderNumber = (value: unknown): number => {
  const parsed =
    typeof value === 'number' ? value : Number.parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getLevelFromAverage = (average: number): string => {
  if (average >= 3) return 'Accomplished B';
  if (average >= 2.5) return 'Accomplished A';
  if (average >= 2) return 'Proficient B';
  if (average >= 1.5) return 'Proficient A';
  return 'Developing';
};

const toDisplayAverage = (average: number): string =>
  (Math.floor((average + 1e-9) * 10) / 10).toFixed(1);

export const getGdadProficiencyDerivedAnswers = (
  answers: Record<string, unknown> | undefined,
): GdadProficiencyDerivedAnswer[] => {
  const role = String(answers?.role ?? '').trim();
  const roleLevel = String(answers?.['role-level'] ?? '').trim();
  const mapKey = `${role}||${roleLevel}`;
  const mappedSkills = gdadSkillsMapping.role_level_skills_map[mapKey] || [];

  const nonZeroScores = mappedSkills
    .slice()
    .sort((a, b) => toOrderNumber(a.order) - toOrderNumber(b.order))
    .map((mappedSkill) => getGdadSkillScore(answers?.[`skill-level-${mappedSkill.skill_id}`]))
    .filter((score) => score > 0);

  const averageScore =
    nonZeroScores.length > 0
      ? nonZeroScores.reduce((total, score) => total + score, 0) / nonZeroScores.length
      : 0;

  const displayedAverage = toDisplayAverage(averageScore);
  const level = getLevelFromAverage(averageScore);
  const levelDescription = levelDescriptionByName[level] || '';
  const levelText = levelDescription ? `${level} - ${levelDescription}` : level;

  return [
    {
      name: 'your-proficiency-score',
      title: 'Your Proficiency Score',
      value: displayedAverage,
    },
    {
      name: 'proficiency-level',
      title: 'Proficiency Level',
      value: levelText,
    },
  ];
};

